import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  Download,
  ExternalLink,
  FlaskConical,
  Loader2,
  RefreshCw,
  WalletCards,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { downloadCsvResponse } from '@/lib/infra/csvExport';
import { apiFetch, useAuthStore } from '@/stores/authStore';
import { useOrgScope } from '@/stores/orgScope';
import { DecimalAlignedMoney } from './DecimalAlignedMoney';
import { HistoricalComparison } from './HistoricalComparison';
import { CashLedgerGate, CASH_LEDGER_UNLOCK_KEY } from './CashLedgerGate';
import {
  CASH_LEDGER_TABLE_HEADER_CLASS,
  CASH_LEDGER_TABLE_ROW_CLASS,
  CashLedgerPageScroll,
  CashLedgerTableViewport,
} from './CashLedgerTableLayout';

interface LedgerEvent {
  eventKey: string;
  transactionHash: string;
  logIndex: number;
  blockNumber: number;
  occurredAt: string;
  cashDeltaMicros: string;
  cashDelta: string;
  classification: string;
  side: string | null;
  title: string | null;
  conditionId: string | null;
  outcome: string | null;
  source: string;
  evidenceStatus: string;
}

interface LedgerSnapshot {
  mode: 'forward-shadow';
  attribution: 'wallet-only';
  currency: 'PUSD';
  wallets: string[];
  scope: null | {
    wallet: string;
    chainId: number;
    startBlock: number;
    lastProcessedBlock: number;
    openingBalance: string;
    netMovement: string;
    ledgerBalance: string;
    observedBalance: string;
    reconciliationDelta: string;
    reconciled: boolean;
    caughtUp: boolean;
    lastSyncedAt: string | null;
    lastError: string | null;
    createdAt: string;
  };
  counts: Array<{ classification: string; count: number }>;
  events: LedgerEvent[];
}

const SYNC_INTERVAL_MS = 15_000;

function money(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(n);
}

function shortWallet(wallet: string): string {
  return `${wallet.slice(0, 8)}…${wallet.slice(-6)}`;
}

function queryString(orgId: string, wallet: string): string {
  const params = new URLSearchParams();
  if (orgId) params.set('orgId', orgId);
  if (wallet) params.set('wallet', wallet);
  const query = params.toString();
  return query ? `?${query}` : '';
}

async function responseError(res: Response): Promise<string> {
  const body = await res.json().catch(() => ({})) as { error?: unknown };
  return typeof body.error === 'string' ? body.error : `Request failed (${res.status})`;
}

function SummaryCard({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint: string;
  tone?: 'neutral' | 'good' | 'bad';
}) {
  const valueClass = tone === 'good'
    ? 'text-emerald-400'
    : tone === 'bad'
      ? 'text-red-400'
      : 'text-foreground';
  return (
    <div className="bg-card border-border rounded-lg border p-4">
      <div className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wide">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${valueClass}`}>{value}</div>
      <div className="text-muted-foreground mt-1 text-[11px]">{hint}</div>
    </div>
  );
}

function EventDirection({ value }: { value: string }) {
  const incoming = !value.startsWith('-') && value !== '0';
  const flat = Number(value) === 0;
  if (flat) return <span className="text-muted-foreground">•</span>;
  return incoming
    ? <ArrowDownLeft size={14} className="text-emerald-400" />
    : <ArrowUpRight size={14} className="text-red-400" />;
}

export function CashLedgerPreview() {
  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem(CASH_LEDGER_UNLOCK_KEY) === 'true',
  );

  if (!unlocked) return <CashLedgerGate onUnlock={() => setUnlocked(true)} />;
  return <CashLedgerPane />;
}

function CashLedgerPane() {
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.user?.role);
  const selectedOrgId = useOrgScope((state) => state.selectedOrgId);
  const orgId = role === 'admin' ? selectedOrgId : '';
  const [snapshot, setSnapshot] = useState<LedgerSnapshot | null>(null);
  const [wallet, setWallet] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);

  const load = useCallback(async (requestedWallet: string) => {
    if (role === 'admin' && !orgId) {
      setSnapshot(null);
      setLoading(false);
      return;
    }
    const gen = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/org/polymarket-cash-ledger${queryString(orgId, requestedWallet)}`);
      if (!res.ok) throw new Error(await responseError(res));
      const data = await res.json() as LedgerSnapshot;
      if (generation.current !== gen) return;
      setSnapshot(data);
      const nextWallet = requestedWallet || data.scope?.wallet || (data.wallets.length === 1 ? data.wallets[0] : '');
      setWallet(nextWallet);
    } catch (err) {
      if (generation.current === gen) setError(err instanceof Error ? err.message : 'Could not load preview');
    } finally {
      if (generation.current === gen) setLoading(false);
    }
  }, [orgId, role]);

  const sync = useCallback(async () => {
    if (!wallet || !snapshot?.scope || syncing) return;
    setSyncing(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/org/polymarket-cash-ledger/sync${queryString(orgId, wallet)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet }),
      });
      if (!res.ok) throw new Error(await responseError(res));
      setSnapshot(await res.json() as LedgerSnapshot);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sync preview');
    } finally {
      setSyncing(false);
    }
  }, [orgId, snapshot?.scope, syncing, wallet]);

  useEffect(() => {
    setWallet('');
    setSnapshot(null);
    void load('');
  }, [load]);

  useEffect(() => {
    if (!snapshot?.scope || !wallet) return;
    const timer = window.setInterval(() => void sync(), SYNC_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [snapshot?.scope, sync, wallet]);

  const start = async () => {
    if (!wallet) return;
    setStarting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/org/polymarket-cash-ledger/enable${queryString(orgId, wallet)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet }),
      });
      if (!res.ok) throw new Error(await responseError(res));
      setSnapshot(await res.json() as LedgerSnapshot);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start preview');
    } finally {
      setStarting(false);
    }
  };

  const exportCsv = async () => {
    if (!wallet || !snapshot?.scope) return;
    setExporting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/org/polymarket-cash-ledger.csv${queryString(orgId, wallet)}`);
      if (!res.ok) throw new Error(await responseError(res));
      // Download the response bytes verbatim (res.blob(), not res.text()) so
      // the relay's BOM survives — a string round-trip UTF-8-decodes it away.
      await downloadCsvResponse(res, 'polymarket-cash-ledger-preview.csv');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not export preview');
    } finally {
      setExporting(false);
    }
  };

  const classifiedCount = useMemo(
    () => snapshot?.counts
      .filter((row) => !row.classification.startsWith('EXTERNAL_') && row.classification !== 'SELF_TRANSFER')
      .reduce((sum, row) => sum + row.count, 0) ?? 0,
    [snapshot?.counts],
  );
  const totalCount = useMemo(
    () => snapshot?.counts.reduce((sum, row) => sum + row.count, 0) ?? 0,
    [snapshot?.counts],
  );

  if (role === 'admin' && !orgId) {
    return (
      <div className="p-6">
        <div className="bg-card border-border rounded-lg border p-8 text-center">
          <WalletCards className="text-muted-foreground mx-auto mb-3" size={28} />
          <p className="text-sm font-medium">Select an organization to inspect its cash-ledger preview.</p>
          <p className="text-muted-foreground mt-1 text-xs">Admin scope is never aggregated across organizations.</p>
        </div>
      </div>
    );
  }

  return (
    <CashLedgerPageScroll>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <FlaskConical size={18} className="text-violet-400" />
            <h1 className="text-lg font-semibold">Polymarket Cash Ledger</h1>
            <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-violet-400">preview</span>
          </div>
          <p className="text-muted-foreground mt-1 max-w-3xl text-xs">
            Read-only historical exports for direct comparison, with the isolated forward-chain verifier retained below.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/organization/org-account')}
            className="border-border text-muted-foreground hover:text-foreground rounded-md border px-3 py-1.5 text-xs"
          >
            Current G1 account
          </button>
          {snapshot?.scope && (
            <>
              <button
                type="button"
                onClick={() => void exportCsv()}
                disabled={exporting}
                className="border-border text-muted-foreground hover:text-foreground flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs disabled:opacity-50"
              >
                {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                Forward CSV
              </button>
              <button
                type="button"
                onClick={() => void sync()}
                disabled={syncing}
                className="flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-500 disabled:opacity-50"
              >
                {syncing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                Sync forward
              </button>
            </>
          )}
        </div>
      </div>

      <div className="border-amber-500/30 bg-amber-500/10 rounded-lg border px-4 py-3 text-xs text-amber-200">
        <div className="flex items-start gap-2">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <p>
            Wallet-scoped, not trader-attributed. Only finalized pUSD transfers after the displayed checkpoint are counted.
            Historical activity before that block remains in the current accounting surfaces.
          </p>
        </div>
      </div>

      {error && (
        <div className="border-red-500/30 bg-red-500/10 rounded-lg border px-4 py-3 text-xs text-red-300">{error}</div>
      )}

      <HistoricalComparison
        orgId={orgId}
        role={role}
        wallet={wallet}
        wallets={snapshot?.wallets ?? []}
        onWalletChange={(nextWallet) => {
          setWallet(nextWallet);
          void load(nextWallet);
        }}
      />

      <div className="pt-2">
        <h2 className="text-sm font-semibold">Forward verification</h2>
        <p className="text-muted-foreground mt-0.5 text-[11px]">
          Optional post-checkpoint chain evidence. It remains separate from the historical comparison above.
        </p>
      </div>

      {loading && !snapshot ? (
        <div className="text-muted-foreground flex items-center justify-center gap-2 py-16 text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading preview…
        </div>
      ) : snapshot && (
        <>
          <div className="bg-card border-border flex flex-wrap items-end gap-3 rounded-lg border p-4">
            <label className="min-w-72 flex-1">
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Authorized funder wallet</span>
              <select
                value={wallet}
                onChange={(event) => {
                  const next = event.target.value;
                  setWallet(next);
                  void load(next);
                }}
                className="bg-background border-border w-full rounded-md border px-3 py-2 font-mono text-xs"
              >
                {snapshot.wallets.length > 1 && <option value="">Select wallet…</option>}
                {snapshot.wallets.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            {!snapshot.scope && (
              <button
                type="button"
                onClick={() => void start()}
                disabled={!wallet || starting}
                className="flex items-center gap-1.5 rounded-md bg-violet-600 px-4 py-2 text-xs font-medium text-white hover:bg-violet-500 disabled:opacity-50"
              >
                {starting ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />}
                Start forward preview
              </button>
            )}
          </div>

          {snapshot.scope && (
            <>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                <SummaryCard label="Opening cash" value={money(snapshot.scope.openingBalance)} hint={`Finalized block ${snapshot.scope.startBlock.toLocaleString()}`} />
                <SummaryCard label="Net movement" value={money(snapshot.scope.netMovement)} hint={`${totalCount.toLocaleString()} confirmed event${totalCount === 1 ? '' : 's'}`} />
                <SummaryCard label="Shadow cash" value={money(snapshot.scope.ledgerBalance)} hint="Opening cash plus observed movement" />
                <SummaryCard label="On-chain cash" value={money(snapshot.scope.observedBalance)} hint={`Observed through block ${snapshot.scope.lastProcessedBlock.toLocaleString()}`} />
                <SummaryCard
                  label="Reconciliation delta"
                  value={money(snapshot.scope.reconciliationDelta)}
                  hint={snapshot.scope.reconciled ? 'Exact at the processed block' : 'Preview is not reconciled'}
                  tone={snapshot.scope.reconciled ? 'good' : 'bad'}
                />
              </div>

              <div className={`rounded-lg border px-4 py-3 text-xs ${snapshot.scope.reconciled
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                : 'border-red-500/30 bg-red-500/10 text-red-300'}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    {snapshot.scope.reconciled ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
                    {snapshot.scope.reconciled
                      ? 'Shadow event sum exactly matches the finalized wallet balance.'
                      : 'Shadow event sum does not match the finalized wallet balance. Do not use this preview as accounting truth.'}
                  </span>
                  <span className="opacity-80">
                    {snapshot.scope.caughtUp ? 'Caught up' : 'Catch-up in progress'} · {classifiedCount.toLocaleString()} classified
                    {snapshot.scope.lastSyncedAt ? ` · ${new Date(snapshot.scope.lastSyncedAt).toLocaleString()}` : ''}
                  </span>
                </div>
              </div>

              <section className="bg-card border-border overflow-hidden rounded-lg border">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <div>
                    <h2 className="text-sm font-semibold">Confirmed wallet events</h2>
                    <p className="text-muted-foreground mt-0.5 text-[11px]">Newest 200 events. Cash signs come from Polygon; labels are best-effort venue enrichment.</p>
                  </div>
                  <code className="text-muted-foreground text-[10px]">{shortWallet(snapshot.scope.wallet)}</code>
                </div>
                <CashLedgerTableViewport testId="cash-ledger-forward-table-scroll">
                  <table className="w-full min-w-[980px] text-left text-xs">
                    <thead className={`${CASH_LEDGER_TABLE_HEADER_CLASS} text-muted-foreground text-[10px] uppercase tracking-wide`}>
                      <tr>
                        <th className="px-4 py-2">Time</th>
                        <th className="px-4 py-2">Type</th>
                        <th className="px-4 py-2">Description</th>
                        <th className="px-4 py-2 text-right">Cash</th>
                        <th className="px-4 py-2">Evidence</th>
                        <th className="px-4 py-2">Transaction</th>
                      </tr>
                    </thead>
                    <tbody className="divide-border divide-y">
                      {snapshot.events.length === 0 ? (
                        <tr><td colSpan={6} className="text-muted-foreground px-4 py-10 text-center">No finalized wallet movement since the checkpoint.</td></tr>
                      ) : snapshot.events.map((event) => (
                        <tr key={event.eventKey} className={`${CASH_LEDGER_TABLE_ROW_CLASS} hover:bg-muted/20`}>
                          <td className="text-muted-foreground whitespace-nowrap px-4 py-2.5">{new Date(event.occurredAt).toLocaleString()}</td>
                          <td className="px-4 py-2.5">
                            <span className="bg-muted rounded px-1.5 py-0.5 text-[10px] font-semibold">{event.classification}</span>
                            {event.side && <span className="text-muted-foreground ml-1 text-[10px]">{event.side}</span>}
                          </td>
                          <td className="max-w-md px-4 py-2.5">
                            <div className="truncate">{event.title || event.outcome || 'Unclassified pUSD transfer'}</div>
                            <div className="text-muted-foreground mt-0.5 truncate font-mono text-[9px]">{event.conditionId || event.eventKey}</div>
                          </td>
                          <td data-money-cell="true" className={`h-[65px] whitespace-nowrap p-0 text-right font-medium tabular-nums ${event.cashDelta.startsWith('-') ? 'text-red-400' : 'text-emerald-400'}`}>
                            <DecimalAlignedMoney
                              value={event.cashDelta}
                              leading={<EventDirection value={event.cashDelta} />}
                            />
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={event.evidenceStatus === 'matched' ? 'text-emerald-400' : 'text-amber-400'}>{event.evidenceStatus}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <a
                              href={`https://polygonscan.com/tx/${event.transactionHash}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 font-mono"
                            >
                              {event.transactionHash.slice(0, 8)}… <ExternalLink size={10} />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CashLedgerTableViewport>
              </section>
            </>
          )}
        </>
      )}
    </CashLedgerPageScroll>
  );
}
